import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, wsConnection } from '@/services/api'
import type { DashboardMetrics, ServerStatus, QueryStats } from '@/types/dashboard'

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const metrics = ref<DashboardMetrics>({
    totalQueries: 0,
    queriesPerSecond: 0,
    cacheHitRate: 0,
    activeZones: 0,
    activeRecords: 0,
    blockedIPs: 0,
  })
  
  const servers = ref<ServerStatus[]>([])
  const queryStats = ref<QueryStats[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdated = ref<Date | null>(null)
  
  // Time range for charts
  const timeRange = ref<'1h' | '6h' | '24h' | '7d' | '30d'>('24h')

  // Computed
  const allServersOnline = computed(() => 
    servers.value.length > 0 && servers.value.every(s => s.status === 'online')
  )

  const hasAnomalies = computed(() => 
    servers.value.some(s => s.anomalyScore && s.anomalyScore > 0.7)
  )

  // Actions
  async function fetchMetrics() {
    loading.value = true
    error.value = null

    try {
      // Fetch real analytics overview
      const analyticsRes = await api.get('/api/v1/analytics/overview')
      const data = analyticsRes.data
      
      metrics.value = {
        totalQueries: data.authoritative?.totalQueries || 0,
        queriesPerSecond: data.authoritative?.queriesPerSecond || 0,
        cacheHitRate: data.authoritative?.packetCacheHits 
          ? ((data.authoritative.packetCacheHits / (data.authoritative.totalQueries || 1)) * 100)
          : parseFloat(data.recursor?.hitRate || 0),
        activeZones: data.zones?.total || 0,
        activeRecords: data.records?.estimatedTotal || 0,
        blockedIPs: 0,
      }
      lastUpdated.value = new Date()

      // Fetch servers in parallel
      try {
        const serversRes = await api.get('/api/v1/system/servers')
        servers.value = serversRes.data || []
      } catch (e) {
        console.warn('Failed to fetch servers:', e)
      }

      // Fetch query stats
      try {
        const statsRes = await api.get(`/api/v1/analytics/query-history?range=${timeRange.value}`)
        const history = statsRes.data?.history || []
        queryStats.value = history.map((h: any) => ({
          timestamp: h.timestamp,
          queries: h.queries || 0,
          cached: h.cached || 0,
        }))
      } catch (e) {
        console.warn('Failed to fetch query stats:', e)
        queryStats.value = generateDemoQueryStats()
      }

    } catch (err: any) {
      error.value = err.response?.data?.message || 'Erro ao carregar métricas'
      console.error('Dashboard fetch error:', err)
      // Fallback to old endpoint
      try {
        const metricsRes = await api.get('/api/v1/dashboard/metrics')
        metrics.value = {
          totalQueries: metricsRes.data.totalQueries || 0,
          queriesPerSecond: metricsRes.data.queriesPerSecond || 0,
          cacheHitRate: metricsRes.data.cacheHitRate || 0,
          activeZones: metricsRes.data.activeZones || 0,
          activeRecords: metricsRes.data.activeRecords || 0,
          blockedIPs: metricsRes.data.blockedIPs || 0,
        }
        lastUpdated.value = new Date()
      } catch (e2) {
        console.error('Fallback also failed:', e2)
      }
    } finally {
      loading.value = false
    }
  }

  // Generate demo stats when API fails
  function generateDemoQueryStats(): QueryStats[] {
    const stats: QueryStats[] = []
    const now = Date.now()
    for (let i = 24; i >= 0; i--) {
      stats.push({
        timestamp: new Date(now - i * 3600000).toISOString(),
        queries: Math.floor(Math.random() * 5000) + 2000,
        cached: Math.floor(Math.random() * 3000) + 1000,
      })
    }
    return stats
  }

  function setTimeRange(range: typeof timeRange.value) {
    timeRange.value = range
    fetchMetrics()
  }

  // Real-time updates via WebSocket
  function subscribeToUpdates() {
    wsConnection.on('metrics:update', (data: Partial<DashboardMetrics>) => {
      metrics.value = { ...metrics.value, ...data }
      lastUpdated.value = new Date()
    })

    wsConnection.on('server:status', (data: ServerStatus) => {
      const index = servers.value.findIndex(s => s.id === data.id)
      if (index >= 0) {
        servers.value[index] = data
      }
    })

    wsConnection.on('query:stats', (data: QueryStats) => {
      queryStats.value.push(data)
      // Keep only last 1000 points
      if (queryStats.value.length > 1000) {
        queryStats.value.shift()
      }
    })
  }

  function unsubscribeFromUpdates() {
    wsConnection.off('metrics:update')
    wsConnection.off('server:status')
    wsConnection.off('query:stats')
  }

  return {
    // State
    metrics,
    servers,
    queryStats,
    loading,
    error,
    lastUpdated,
    timeRange,
    // Computed
    allServersOnline,
    hasAnomalies,
    // Actions
    fetchMetrics,
    setTimeRange,
    subscribeToUpdates,
    unsubscribeFromUpdates,
  }
})
